import { useSnackbar } from 'notistack';
import * as React from 'react';
import * as Yup from 'yup';
import { useCurrentOrganisation } from '../../../../../../../../app/CurrentOrganisationProvider';
import { Dialog, DialogProps } from '../../../../../../../../components/Dialog';
import { errorSnackbar } from '../../../../../../../../components/ErrorSnackbar';
import { SelectionMenu } from '../../../../../../../../components/SelectionMenu';
import { Form, InputComponentProps, OnSubmit } from '../../../../../../../../design-system';
import {
  CommandChangeAvailability,
  usePostChargePointCommandChangeAvailability,
} from '../../../../../../../../utils/api';
import { createSchema } from '../../../../../../../../utils/formUtils';
import { useChargePoint } from '../../../Provider';

type AvailabilityFormFields = { availability: string };

const initialValues: AvailabilityFormFields = {
  availability: '',
};

const validationSchema = createSchema<AvailabilityFormFields>({
  availability: Yup.string().required('Availability is required'),
});

export const ChangeAvailabilityDialog: React.FC<DialogProps> = ({ isOpen, onClose }: DialogProps) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { slug } = useCurrentOrganisation().organisation ?? { slug: '' };
  const { chargePoint, refetch } = useChargePoint();
  const { id: chargePointId } = chargePoint ?? { id: '' };

  const { error, mutate } = usePostChargePointCommandChangeAvailability({
    organisationSlug: slug,
    chargePointId,
  });

  React.useEffect(() => {
    if (error) {
      errorSnackbar('Unable to change charge point availability', closeSnackbar, enqueueSnackbar, error);
    }
  }, [closeSnackbar, enqueueSnackbar, error]);

  const onSubmit = React.useCallback<OnSubmit<AvailabilityFormFields>>(
    async ({ availability }, { setSubmitting }) => {
      try {
        const { data } = await mutate({
          connectorId: 0,
          changeAvailabilityType: availability as CommandChangeAvailability['changeAvailabilityType'],
        });
        enqueueSnackbar(`Change availability command ${data.status.toLowerCase()}`);
        onClose();
        refetch();
      } finally {
        setSubmitting(false);
      }
    },
    [mutate, enqueueSnackbar, onClose, refetch],
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Change availability">
      <Form<AvailabilityFormFields>
        fields={[
          {
            id: 'availability',
            label: 'Availability',
            inputComponent: ({
              disabled,
              error: availabilityFieldError,
              helperText,
              name,
              onBlur,
              onChange,
              value,
            }: InputComponentProps<AvailabilityFormFields['availability']>) => (
              <SelectionMenu
                defaultValue={value}
                disabled={disabled}
                error={availabilityFieldError}
                helperText={helperText}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                options={[
                  { value: 'Inoperative', label: 'Disabled' },
                  { value: 'Operative', label: 'Enabled' },
                ]}
              />
            ),
          },
        ]}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        labels={{ save: 'Send command' }}
        isEditing
      />
    </Dialog>
  );
};
